import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { SmartLinkWidgetType } from 'fym-shared/src/smartlinks/enums';
import { BioLinkAutoAddResult } from 'fym-shared/src/smartlinks/bioLinkSettings';

/**
 * The Bio Link row of Done (design smartlink-editor e2).
 * - `live`: the song's Catalog links block is on the live page and in the draft.
 * - `notLive`: the block is in the draft only (a page that was never published, or a draft that waits for Publish).
 * - `draftRemoved`: the live page shows the block, the draft has no block (an Undo that did not publish).
 * - `absent`: the song has no block on the Bio Link.
 */
export type DoneBioLinkState =
  | { kind: 'live'; widgetUuid: string }
  | { kind: 'notLive'; widgetUuid: string }
  | { kind: 'draftRemoved' }
  | { kind: 'absent' };

type Widgets = SerializedSmartLink['recentSmartLinkVersion']['widgets'];

function songBlockOf(widgets: Widgets, itemUuid: string): string | undefined {
  return (widgets ?? []).find(
    (widget) => widget.type === SmartLinkWidgetType.presave && widget.data.catalogItemUuid === itemUuid
  )?.uuid;
}

/** The row state, read from the Bio Link page itself, so it stays true after Undo, Add and a reload. */
export function doneBioLinkState(smartLink: SerializedSmartLink, itemUuid: string): DoneBioLinkState {
  const draftBlock = songBlockOf(smartLink.recentSmartLinkVersion.widgets, itemUuid);
  const liveBlock = songBlockOf(smartLink.publishedSmartLinkVersion?.widgets ?? null, itemUuid);
  if (draftBlock !== undefined) {
    return liveBlock === undefined
      ? { kind: 'notLive', widgetUuid: draftBlock }
      : { kind: 'live', widgetUuid: draftBlock };
  }
  return liveBlock === undefined ? { kind: 'absent' } : { kind: 'draftRemoved' };
}

/**
 * The Bio Link page of the row. The create answer names it. With no answer in memory (a reload of
 * Done), the Bio Link settings name it. Null: no row (no Bio Link, the demo artist, an older API).
 */
export function doneBioLinkPageId(
  result: BioLinkAutoAddResult | undefined,
  settingsPageId: number | null | undefined
): number | null {
  if (result === undefined) {
    return settingsPageId ?? null;
  }
  if (result.skippedReason === 'noBioLink' || result.skippedReason === 'demo') {
    return null;
  }
  return result.smartLinkId;
}
